// Site AI settings — /websites/:id/settings/ai
//
// One section per task type; each section is a ModelPicker that writes to
// website_ai_preferences. Persisted on change (no Save button) so the page
// is "live"; toasts confirm. Reset link clears the row and falls back to
// org defaults.
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/hooks/useCompany";
import { supabase } from "@/integrations/supabase/client";
import { ModelPicker } from "@/components/ai/ModelPicker";
import { getSitePreferences, upsertSitePreference, type TaskType } from "@/lib/credits/api";

const TASKS: TaskType[] = ["rewrite", "image", "translate", "qa", "seo", "score"];

export default function SiteAiSettingsPage() {
  const { id: websiteId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentCompanyId } = useCompany();
  const qc = useQueryClient();
  const { toast } = useToast();

  const site = useQuery({
    queryKey: ["website-detail-min", websiteId],
    enabled: !!websiteId,
    queryFn: async () => {
      // deno-lint-ignore no-explicit-any
      const db = supabase as any;
      const { data, error } = await db.from("websites").select("id, name, company_id").eq("id", websiteId).maybeSingle();
      if (error) throw error;
      return data as { id: string; name: string; company_id: string } | null;
    },
  });

  const prefs = useQuery({
    queryKey: ["site-ai-prefs", websiteId],
    enabled: !!websiteId,
    queryFn: () => getSitePreferences(websiteId!),
  });

  const byTask = useMemo(() => {
    const map = new Map<string, { preferred_model_key: string | null; fallback_model_key: string | null }>();
    for (const row of prefs.data ?? []) {
      map.set(row.task_type, { preferred_model_key: row.preferred_model_key, fallback_model_key: row.fallback_model_key });
    }
    return map;
  }, [prefs.data]);

  async function setPrimary(task: TaskType, modelKey: string | null) {
    if (!websiteId) return;
    const existing = byTask.get(task);
    try {
      await upsertSitePreference(websiteId, task, modelKey, existing?.fallback_model_key ?? null);
      toast({ description: t("models.siteSavedToast") });
      qc.invalidateQueries({ queryKey: ["site-ai-prefs", websiteId] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    }
  }
  async function setFallback(task: TaskType, modelKey: string | null) {
    if (!websiteId) return;
    const existing = byTask.get(task);
    if (!existing?.preferred_model_key) return;  // no primary, no fallback
    try {
      await upsertSitePreference(websiteId, task, existing.preferred_model_key, modelKey);
      toast({ description: t("models.siteSavedToast") });
      qc.invalidateQueries({ queryKey: ["site-ai-prefs", websiteId] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    }
  }

  if (site.isLoading || prefs.isLoading) {
    return (
      <div className="container mx-auto py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("featureGate.loading")}
      </div>
    );
  }

  const orgId = currentCompanyId ?? site.data?.company_id ?? null;
  if (!orgId || !site.data) return <div className="container mx-auto py-8">Site not found.</div>;

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/websites/${websiteId}`}><ArrowLeft className="h-4 w-4 mr-1" /> {site.data.name}</Link>
        </Button>
      </div>
      <div>
        <h1 className="text-2xl font-bold">{t("models.pickerTitle")} — {site.data.name}</h1>
        <p className="text-sm text-muted-foreground">{t("models.pickerSubtitle")}</p>
      </div>

      {TASKS.map((task) => {
        const current = byTask.get(task);
        return (
          <Card key={task}>
            <CardHeader>
              <CardTitle className="text-base">{t(`models.${task}`)}</CardTitle>
              <CardDescription className="text-xs">
                {t("models.pickerSubtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ModelPicker
                orgId={orgId}
                siteId={websiteId}
                taskType={task}
                primary={current?.preferred_model_key ?? null}
                fallback={current?.fallback_model_key ?? null}
                onPrimaryChange={(k) => setPrimary(task, k)}
                onFallbackChange={(k) => setFallback(task, k)}
                mode="site"
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
